import { buildParams } from '../../helpers/path';
import { PaginationRequest, PaginationType } from '../../types/pagination';
import {
  CustomCacheRequest,
  DefaultRequest,
  InfiniteScrollRequest,
  LanguageRequest,
  SortRequest,
} from '../../types/props';
import { EmployeeType } from './employees-api';
import { managerApiUrl } from './manager-api';
import { ShiftType } from './shifts-api';
import { baseFetch } from './utils.ts/base-fetch';
import { downloadFile, fileFetch } from './utils.ts/file-fetch';

export type CheckinHistoryType = {
  id: number;
  checkin_staff_name: string;
  old_start_time: string;
  old_end_time: string;
  new_start_time: string | null;
  new_end_time: string | null;
  updated_by_name: string;
  updated_at: string;
  reason: string;
  action: 'update' | 'delete';
};

export type CheckInType = {
  id: number;
  staff: Pick<EmployeeType, 'id' | 'name' | 'surname' | 'tags' | 'places'>;
  start_time: string | null;
  end_time: string | null;
  shift: ShiftType | null;
  checkins?: CheckInType[];
};

export type FetchCheckInCheckOutRequest = {
  with_archive?: boolean;
  employee?: string;
  date?: string;
  from?: string;
  to?: string;
  checkin?: string;
  checkout?: string;
  delay?: string;
  delay_check?: boolean;
  overtime?: string;
  overtime_check?: boolean;
  only_open_shifts_check?: boolean;
  only_closed_shifts_check?: boolean;
  tags?: string;
  branches?: string;
} & CustomCacheRequest &
  DefaultRequest &
  PaginationRequest &
  SortRequest &
  LanguageRequest &
  InfiniteScrollRequest;

export type CheckInExportFormatType = 'excel' | 'pdf';

export type CheckInCheckOutExportRequest = FetchCheckInCheckOutRequest & {
  format: CheckInExportFormatType;
};

export type PutCheckInCheckOutRequest = {
  checkin_id: number;
  start_time: string;
  end_time: string;
  reason: string;
};

export type CheckInStatsType = {
  total: number;
  on_shift: number;
  delays: number;
  no_delays: number;
  absent: number;
  overtime: number;
};

export type FetchCheckInCheckOutHistoryRequest = {
  historyDate?: string;
  historyEmployee?: string;
  historyAuthor?: string;
  historyReason?: string;
} & CustomCacheRequest &
  DefaultRequest &
  PaginationRequest &
  SortRequest;

export const fetchCheckInCheckOut = async (
  params: FetchCheckInCheckOutRequest,
): Promise<
  {
    checkins: CheckInType[];
  } & PaginationType
> => {
  return await baseFetch(`${managerApiUrl}/checkin${buildParams(params)}`);
};

export const fetchCheckInCheckOutExport = async (params: CheckInCheckOutExportRequest) => {
  const defaultFileNames = {
    excel: 'CheckInCheckOut.xlsx',
    pdf: 'CheckInCheckOut.pdf',
  };

  const { blob, fileName } = await fileFetch(
    `${managerApiUrl}/checkin/export${buildParams(params)}`,
  );

  downloadFile(blob, fileName || defaultFileNames[params.format]);
};

export const putCheckInCheckOut = async (params: PutCheckInCheckOutRequest) => {
  return await baseFetch(`${managerApiUrl}/checkin`, {
    method: 'PUT',
    body: JSON.stringify(params),
  });
};

export const deleteCheckin = async (id: string, reason: string) => {
  return await baseFetch(`${managerApiUrl}/checkin/${id}`, {
    method: 'DELETE',
    body: JSON.stringify({ reason }),
  });
};

export const fetchCheckInCheckOutHistory = async (
  params: FetchCheckInCheckOutHistoryRequest,
): Promise<{ history: CheckinHistoryType[] } & PaginationType> => {
  return await baseFetch(`${managerApiUrl}/checkin-history${buildParams(params)}`);
};
